import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { IError } from "../../utils/api.types";
import { ITag, ITagDeleteResponse, ITagFormInputs, ITagUpdatePayload } from "../../utils/tag.types";

/**
 * Fetch the tag master for the logged-in account.
 * The account is resolved from the JWT server-side, so no id is passed.
 * Omitting `appliesTo` returns every tag (Invoice + SKU + generic).
 */
export const fetchTags = createAsyncThunk(
    'tag/fetchTags',
    async (query: string | undefined): Promise<ITag[]> => {
        const response = await axios.get(`/api/v1/tag${query ? `?${query}` : ''}`);

        return response.data;
    }
);

/**
 * Fetch a single tag by id.
 */
export const fetchTagById = createAsyncThunk(
    'tag/fetchTagById',
    async (id: string): Promise<ITag> => {
        const response = await axios.get(`/api/v1/tag/${id}`);

        return response.data;
    }
);

/**
 * Add Tag async thunk.
 * The API answers 400 on a duplicate name, so surface the server message.
 */
export const addTag = createAsyncThunk<
    ITag,
    ITagFormInputs,
    {
        rejectValue: IError,
    }
>(
    'tag/addTag',
    async (tagPayload: ITagFormInputs, thunkApi) => {
        try {
            const response = await axios.post(`/api/v1/tag`, tagPayload);

            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response?.data);
        }
    }
);

/**
 * Update Tag async thunk.
 */
export const updateTag = createAsyncThunk<
    ITag,
    ITagUpdatePayload,
    {
        rejectValue: IError,
    }
>(
    'tag/updateTag',
    async ({ id, tagPayload }: ITagUpdatePayload, thunkApi) => {
        try {
            const response = await axios.put(`/api/v1/tag/${id}`, tagPayload);

            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response?.data);
        }
    }
);

/**
 * Delete Tag async thunk.
 * This is a soft delete server-side — the tag is set to `inactive` and returned,
 * so records that already reference it keep working.
 */
export const deleteTag = createAsyncThunk(
    'tag/deleteTag',
    async (id: string): Promise<ITagDeleteResponse> => {
        const response = await axios.delete(`/api/v1/tag/${id}`);

        return response.data;
    }
);
